// SYSTEM_ROUTE: /runtime
import { useRef, useState } from 'react';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import { exportSnapshot, importSnapshot, systemClock, InvalidSnapshotError, SyncEngine, LoopbackSyncTarget } from '@life-os/data';
import { capabilityAvailability, capabilityReason, connectivitySummary } from '@life-os/app-core';
import { useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { appFlags } from '../lib/config.js';
import { useConnectivity } from '../lib/useConnectivity.js';
import { buildSyncTarget } from '../lib/sync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const CAPABILITY_LABELS: Record<string, string> = {
  cloudSync: 'Cloud sync',
  cloudBackup: 'Cloud backup',
  ai: 'AI assistant',
  email: 'Email delivery',
  telegram: 'Telegram bridge',
  whatsapp: 'WhatsApp',
  voiceCalls: 'Voice calls',
  webPush: 'Push notifications',
};

const STATUS_TONE: Record<string, 'primary' | 'warn' | 'neutral'> = {
  available: 'primary',
  degraded: 'warn',
  'unavailable-offline': 'warn',
  'not-configured': 'neutral',
};

export function RuntimeScreen() {
  const { data, getRuntimeStatus, revision, refresh } = useStore();
  const status = useAsync(() => getRuntimeStatus(), [revision]);
  const pending = useAsync(() => data.queue.pending(), [revision]);
  const connectivity = useConnectivity();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [backupMsg, setBackupMsg] = useState('');
  const [backupTone, setBackupTone] = useState<'ok' | 'warn'>('ok');
  const [syncMsg, setSyncMsg] = useState('');

  const s = status.value;
  const muts = pending.value ?? [];
  const caps = capabilityAvailability(appFlags, connectivity);
  const connSummary = connectivitySummary(appFlags, connectivity);

  const runLoopbackSync = async () => {
    // Prefer the configured cloud target; fall back to a loopback target for
    // the offline demo so the button always does something visible.
    const target = buildSyncTarget() ?? new LoopbackSyncTarget();
    const outcome = await new SyncEngine(data.store, target, systemClock).sync();
    const parts = [`${outcome.pushed} pushed`, `${outcome.pulled} pulled`];
    if (outcome.conflicted > 0) parts.push(`${outcome.conflicted} need review`);
    if (outcome.skipped > 0) parts.push(`${outcome.skipped} kept local`);
    setSyncMsg(`Synced to ${target.name} — ${parts.join(', ')}.`);
    refresh();
  };

  const exportBackup = async () => {
    const snapshot = await exportSnapshot(data.store, systemClock);
    const blob = new Blob([JSON.stringify(snapshot, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `life-os-backup-${snapshot.exportedAt.slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
    const count = Object.values(snapshot.collections).reduce((n, c) => n + c.length, 0);
    setBackupTone('ok');
    setBackupMsg(`Exported ${count} record(s).`);
  };

  const onImportFile = async (file: File) => {
    try {
      const parsed = JSON.parse(await file.text());
      const { restored } = await importSnapshot(data.store, parsed);
      setBackupTone('ok');
      setBackupMsg(`Restored ${restored} record(s). Reloading…`);
      refresh();
      setTimeout(() => window.location.reload(), 600);
    } catch (err) {
      setBackupTone('warn');
      setBackupMsg(err instanceof InvalidSnapshotError ? err.message : 'Could not read that backup file.');
    }
  };

  return (
    <>
      <ScreenHeader title={<>Runtime Status</>} subtitle={<>Trust local-first storage, sync, backup, and platform health.</>} />

      <div className="cols-2">
        <Pane title="Local-first health">
          <Stack>
            <CompactRow title="Local database" subtitle={s?.localDb ?? '…'} />
            <CompactRow title="Pending mutations" subtitle={String(s?.pendingMutations ?? 0)} />
            <CompactRow title="Conflicts to review" subtitle={String(s?.conflicts ?? 0)} />
            <CompactRow title="Last sync" subtitle={s?.lastSyncAt ?? 'Local only (no cloud configured)'} />
          </Stack>
        </Pane>

        <Pane title="Platform availability">
          <Stack>
            {s
              ? Object.entries(s.platformCapabilities).map(([p, a]) => (
                  <CompactRow key={p} title={p} subtitle={a} />
                ))
              : null}
          </Stack>
        </Pane>
      </div>

      <Pane
        title="Connected capabilities"
        hint={
          connectivity === 'online'
            ? `Online · ${connSummary.available} ready, ${connSummary.notConfigured} not configured`
            : `Offline · core works; ${connSummary.unavailableOffline} feature(s) need internet`
        }
      >
        <Stack>
          {caps.map((c) => (
            <CompactRow
              key={c.id}
              title={CAPABILITY_LABELS[c.id] ?? c.id}
              subtitle={`${c.tier} · ${capabilityReason(c)}`}
              meta={<Chip tone={STATUS_TONE[c.status]}>{c.status.replace(/-/g, ' ')}</Chip>}
            />
          ))}
        </Stack>
      </Pane>

      <Pane title="Backup & Restore" hint="Local-first data portability — your data, in one file.">
        <Stack>
          <CompactRow
            title="Export a full backup"
            subtitle="Download every record as a single checksummed JSON file."
            meta={<Button variant="primary" onClick={() => void exportBackup()}>Export</Button>}
          />
          <CompactRow
            title="Restore from a backup"
            subtitle="Validates integrity before merging records back in, then reloads."
            meta={<Button variant="ghost" onClick={() => fileRef.current?.click()}>Import</Button>}
          />
          {backupMsg ? <Chip tone={backupTone === 'ok' ? 'primary' : 'warn'}>{backupMsg}</Chip> : null}
          <input
            ref={fileRef}
            type="file"
            accept="application/json,.json"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void onImportFile(file);
              e.target.value = '';
            }}
            aria-label="Backup file"
          />
        </Stack>
      </Pane>

      <Pane title="Outbox" hint={`${muts.length} change(s) queued for sync`}>
        <Stack>
          <CompactRow
            title="Reconcile now"
            subtitle="Drains the outbox against a loopback target. Strict-local data never leaves the device."
            meta={<Button variant="primary" onClick={() => void runLoopbackSync()}>Sync now</Button>}
          />
          {syncMsg ? <Chip tone="primary">{syncMsg}</Chip> : null}
          {muts.length === 0 ? (
            <EmptyState title="Outbox empty">All local changes are settled. Strict-local data never leaves the device.</EmptyState>
          ) : (
            muts.map((m) => (
              <CompactRow key={String(m.localId)} title={`${m.op} ${m.kind}`} subtitle={`${m.syncState} · ${new Date(m.createdAt).toLocaleTimeString()}`} />
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
